import React, { Component, ReactNode } from 'react'
import styled, { CSSProperties, StyledComponent } from 'styled-components'

import { SIZES, COLORS } from '~/common/globalStyles'
import Icon from '~/views/components/ui/Icon'
import { IconNames } from '~/views/components/ui/Icon/Icons'

import HelpTooltip from '../Tooltip/HelpTooltip'

const CheckboxIcon = styled(Icon)`
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
`

const DefaultLabel = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  z-index: 1;
`

interface CheckboxState {
  checked: boolean | undefined
}

export interface CheckboxProps {
  className?: string
  checked?: boolean
  label?: string | ReactNode
  labelElement?: StyledComponent<'label', any, any, any> // https://github.com/guidecx/gcx-web/pull/2704/files/7015709f286e7eee71f0826298bfcb797dfc17a6#r680261384
  style?: CSSProperties
  size?: keyof typeof SIZES
  onChange?:
    | (() => false)
    | ((event: React.ChangeEvent<HTMLInputElement>) => void)
  readOnly?: boolean
  disabled?: boolean
  name?: string
  value?: string
  onMouseDown?: () => void
  tooltipText?: string
  notClickable?: boolean
  boldOutline?: boolean
  formStyle?: {
    height: number
    width: number
  }
  checkedIcon?: IconNames
  uncheckedIcon?: IconNames
  dataTestId?: string
  checkBoxColor?: keyof typeof COLORS
}

class Checkbox extends Component<CheckboxProps, CheckboxState> {
  override state = {
    checked: this.props.checked || false,
  }

  override componentDidUpdate(
    _prevProps: CheckboxProps,
    prevState: CheckboxState,
  ) {
    if (prevState.checked !== this.props.checked) {
      this.setState({ checked: this.props.checked })
    }
  }

  override render() {
    const {
      label,
      labelElement,
      style,
      size = 'xl',
      onChange = () => false,
      readOnly,
      disabled,
      name,
      onMouseDown,
      tooltipText,
      boldOutline,
      className,
      checkedIcon = 'checkbox-checked',
      uncheckedIcon = 'checkbox-unchecked',
      dataTestId,
      checkBoxColor,
    } = this.props
    const { checked } = this.state
    const Label = labelElement || DefaultLabel
    const checkboxIcon = checked ? checkedIcon : uncheckedIcon
    let checkBoxDisplayColor: keyof typeof COLORS = checked
      ? 'blue'
      : 'mediumGray'
    if (checkBoxColor) {
      checkBoxDisplayColor = checkBoxColor
    }

    return (
      <Label className={className} style={style} onMouseDown={onMouseDown}>
        <input
          data-testid={dataTestId}
          name={name}
          type="checkbox"
          disabled={disabled}
          checked={checked}
          readOnly={readOnly}
          onChange={onChange}
          style={{ opacity: 0, cursor: 'pointer' }}
          hidden
        />
        <CheckboxIcon
          disabled={disabled}
          name={checkboxIcon}
          color={COLORS[checkBoxDisplayColor]}
          width={SIZES[size]}
          boldOutline={boldOutline}
          data-testid={checked ? 'toggleChecked' : 'toggleNotChecked'}
        />
        <span style={{ marginLeft: label ? SIZES.small : 'inherit' }}>
          {label}
          {tooltipText && <HelpTooltip title={tooltipText} />}
        </span>
      </Label>
    )
  }
}

export default Checkbox
